import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { CardChrome } from './_CardChrome';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { forgotPassword } from '../../services/api/externalAuth';
import { forgotPasswordSchema, type ForgotPasswordInput } from './_schemas';

/**
 * Forgot-password page.
 *
 * Anti-enumeration (T-15-02-05): the backend always returns 200 regardless of
 * whether the email matches a known external user. The UI mirrors that — any
 * 200 transitions to the in-place success state. Only network/5xx surface as
 * the generic banner.
 */
export function ForgotPasswordPage() {
  const [submitted, setSubmitted] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const successRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (submitted && successRef.current) {
      successRef.current.focus();
    }
  }, [submitted]);

  const onSubmit = async (values: ForgotPasswordInput) => {
    setBannerError(null);
    try {
      await forgotPassword(values);
      setSubmitted(true);
    } catch {
      // Backend always returns 200 — only network/5xx surface here.
      setBannerError('Something went wrong, please try again.');
    }
  };

  if (submitted) {
    return (
      <CardChrome>
        <div ref={successRef} tabIndex={-1} role="status" aria-live="polite">
          <p className="text-center text-gray-700">
            If that email exists, we&apos;ve sent a reset link. Check your inbox (and spam folder).
          </p>
          <Link
            to="/external/login"
            className="mt-4 block text-center text-sm text-primary-600 hover:text-primary-700"
          >
            Back to sign in
          </Link>
        </div>
      </CardChrome>
    );
  }

  return (
    <CardChrome>
      {bannerError && (
        <div
          role="alert"
          className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4"
        >
          {bannerError}
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Input
          label="Email address"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register('email')}
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          isLoading={isSubmitting}
          className="w-full mt-6"
        >
          Send reset link
        </Button>
      </form>
      <Link
        to="/external/login"
        className="mt-4 block text-center text-sm text-primary-600 hover:text-primary-700"
      >
        Back to sign in
      </Link>
    </CardChrome>
  );
}
